import React, { useState } from 'react';
import SummaryBox from './SummaryBox';

const SalarySelector = ({
  label,
  selectedTier,
  setSelectedTier,
  salaryTiers,
  getCustomDescription,
  adjustedAmount,
  adjustmentReason,
  additionalInfo,
  isEaAdjusted = false,
  eaType,
  selectorConfig,
  location,
  applyLocationMultiplier,
  summaryBoxInfo
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const selectedTierData = salaryTiers[selectedTier];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate monthly salary with location consideration
  const getMonthlyRate = (annualAmount) => {
    const adjustedAmount = applyLocationMultiplier ? applyLocationMultiplier(annualAmount) : annualAmount;
    return adjustedAmount / 12;
  };

  // Safely extract description parts
  const getDescriptionDetail = (description) => {
    if (!description) return '';
    const parts = description.split('-');
    return parts.length > 1 ? parts[1].trim() : description;
  };

  // Dynamic EA info based on current state
  const getEaInfoText = () => {
    if (isEaAdjusted && eaType !== 'none') {
      return selectorConfig.eaAdjustmentText;
    }
    return "";
  };

  // Get the actual salary amount after adjustments (if any)
  const getActualSalary = () => {
    if (isEaAdjusted && eaType !== 'none') {
      return selectedTierData.amount - (selectorConfig.eaSavingsAmount || 20000);
    }
    return selectedTierData.amount;
  };

  // Display salary with location consideration
  const displaySalary = (amount) => {
    const adjustedAmount = applyLocationMultiplier ? applyLocationMultiplier(amount) : amount;

    if (location === 'sanfrancisco') {
      return `${formatCurrency(amount)} (${formatCurrency(adjustedAmount)} ${selectorConfig.inSFText})`;
    }

    return formatCurrency(amount);
  };

  // Prepare summary items for the SummaryBox
  const summaryItems = [];
  const actualSalary = getActualSalary();
  const adjustedSalary = applyLocationMultiplier ? applyLocationMultiplier(actualSalary) : actualSalary;
  const originalSalary = selectedTierData.amount;
  const adjustedOriginalSalary = applyLocationMultiplier ? applyLocationMultiplier(originalSalary) : originalSalary;

  // Add monthly cost
  if (isEaAdjusted && eaType !== 'none') {
    summaryItems.push({
      label: selectorConfig.monthlyBaseLabel,
      value: formatCurrency(getMonthlyRate(originalSalary)),
      type: "cost",
      bold: false
    });
    summaryItems.push({
      label: selectorConfig.monthlyAdjustedLabel,
      value: formatCurrency(getMonthlyRate(actualSalary)),
      type: "cost",
      bold: false
    });
  } else {
    summaryItems.push({
      label: selectorConfig.monthlyLabel,
      value: formatCurrency(getMonthlyRate(originalSalary)),
      type: "cost",
      bold: false
    });
  }

  // Add annual cost
  summaryItems.push({
    label: isEaAdjusted && eaType !== 'none' ? selectorConfig.annualBaseLabel : selectorConfig.annualLabel,
    value: formatCurrency(adjustedOriginalSalary),
    type: "cost",
    bold: false
  });

  // Add adjusted annual cost if applicable
  if (isEaAdjusted && eaType !== 'none') {
    summaryItems.push({
      label: selectorConfig.annualAdjustedLabel,
      value: formatCurrency(adjustedSalary),
      type: "cost",
      bold: false
    });
  }

  // Add custom adjusted amount if provided
  if (!isEaAdjusted && adjustedAmount) {
    const adjustedDisplayAmount = applyLocationMultiplier ? applyLocationMultiplier(adjustedAmount) : adjustedAmount;
    summaryItems.push({
      label: selectorConfig.adjustedLabel,
      value: `${formatCurrency(adjustedDisplayAmount)} ${adjustmentReason ? `(${adjustmentReason})` : ''}`,
      type: "cost",
      bold: false
    });
  }

  // Add success modifier
  summaryItems.push({
    label: selectorConfig.successModifierLabel,
    value: selectedTierData.modifier > 0 ? `+${selectedTierData.modifier}%` : `${selectedTierData.modifier}%`,
    type: "success",
    bold: true
  });

  return (
    <div className="mb-4">
      <div className="flex items-center mb-1">
        <h3 className="font-medium">{label}</h3>
        <button
          className="ml-2 text-blue-500 text-sm"
          onClick={() => setShowInfo(!showInfo)}
        >
          {showInfo ? selectorConfig.hideDetailsLabel : selectorConfig.showDetailsLabel}
        </button>
      </div>

      {showInfo && (
        <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
          <p className="mb-2">
            {selectorConfig.salaryImpactText}
          </p>
          {isEaAdjusted && getEaInfoText() && <p className="mb-2">{getEaInfoText()}</p>}
          {additionalInfo && <p className="mb-2">{additionalInfo}</p>}
          <ul className="list-disc pl-5">
            {salaryTiers.map((tier, idx) => (
              <li key={idx} className={selectedTier === idx ? "font-semibold" : ""}>
                <strong>{selectorConfig.tierPrefix} {idx}:</strong>{' '}
                {location === 'sanfrancisco'
                  ? `${formatCurrency(tier.amount)} (${formatCurrency(applyLocationMultiplier(tier.amount))} ${selectorConfig.inSFText})`
                  : formatCurrency(tier.amount)}
                {(isEaAdjusted && eaType !== 'none' && idx === selectedTier) &&
                  ` (${formatCurrency(applyLocationMultiplier(tier.amount - (selectorConfig.eaSavingsAmount || 20000)))} ${selectorConfig.eaDiscountText})`
                } -
                {getDescriptionDetail(tier.detailedDescription)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <select
        value={selectedTier}
        onChange={(e) => setSelectedTier(Number(e.target.value))}
        className="w-full p-2 border rounded"
      >
        {salaryTiers.map((tier, index) => {
          let displayText = getCustomDescription && index === selectedTier
            ? getCustomDescription()
            : tier.description;

          // For San Francisco, show adjusted salary in the dropdown
          if (location === 'sanfrancisco') {
            const baseSalary = tier.amount;
            const adjustedSalary = applyLocationMultiplier(baseSalary);
            const salaryPart = displayText.split(':')[1] ? displayText.split(':')[1].trim() : '';
            const labelPart = displayText.split(':')[0];
            displayText = `${labelPart}: ${formatCurrency(adjustedSalary)} (${selectorConfig.inSFText})`;
          }

          return (
            <option key={index} value={index}>
              {displayText}
            </option>
          );
        })}
      </select>

      {/* Replace the old summary div with SummaryBox */}
      <SummaryBox
        title={getDescriptionDetail(selectedTierData.detailedDescription)}
        items={summaryItems}
        summaryBoxInfo={summaryBoxInfo}
      />
    </div>
  );
};

export default SalarySelector;